/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { FC, useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'

import { FiDownload, FiRefreshCw, FiTrash2, FiPlayCircle } from 'react-icons/fi'
import { AlertDialogCustom } from '../../Components/AlertDialog'
import { Header } from '../../Components/Header'
import { SearchBox } from '../../Components/Header/SearchBox'
import { Pagination } from '../../Components/Pagination'
import { Sidebar } from '../../Components/Sidebar'
import { Can } from '../../Components/Can'
import api from '../../Services/api'
import { format } from 'date-fns'
import debounce from 'lodash.debounce'

interface QueueJob {
  id: string
  message_id: string
  job_name: string
  process_prefecture_id: string
  process_id: string
  prefecture_code: string
  prefecture: string
  payload: string
  status: string
  attempts: number
  last_error: string
  queued_at: string
  started_at: string
  finished_at: string
  created_at: string
}

interface Stats {
  counts: {
    queue: number
    errors: number
    dlq: number
    completed: number
  }
  sqs: {
    queue: { visible: number, inFlight: number }
    dlq: { visible: number }
  } | null
}

const TABS = ['queue', 'errors', 'dlq']

const STATUS_LABEL: { [key: string]: string } = {
  queued: 'Na fila',
  processing: 'Processando',
  failed: 'Erro',
  dlq: 'DLQ',
  completed: 'Concluído',
  retried: 'Reprocessado',
  purged: 'Removido'
}

const STATUS_COLOR: { [key: string]: string } = {
  queued: 'blue',
  processing: 'orange',
  failed: 'red',
  dlq: 'red',
  completed: 'green',
  retried: 'purple',
  purged: 'gray'
}

const formatDate = (date?: string) =>
  date ? format(new Date(date), 'dd/MM/yyyy HH:mm') : '-'

const QueuesList: FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [data, setData] = useState<QueueJob[]>([])
  const [total, setTotal] = useState(1)
  const [stats, setStats] = useState<Stats | null>(null)
  const [selectedItem, setSelectedItem] = useState<QueueJob | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [purging, setPurging] = useState(false)
  const [reprocessing, setReprocessing] = useState(false)
  const [confirmPurge, setConfirmPurge] = useState(false)
  const [confirmReprocess, setConfirmReprocess] = useState(false)
  const toast = useToast()

  const tab = TABS[tabIndex]

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/queue-jobs/stats')
      setStats(data)
    } catch (error) {
      console.log('erro ao pegar estatísticas da fila: ', error)
    }
  }, [])

  const load = useCallback(async (pag: number, currentTab: string, searchValue: string) => {
    setLoading(true)
    try {
      const { data } = await api.get('/queue-jobs', {
        params: {
          pag,
          tab: currentTab,
          search: searchValue || undefined
        }
      })

      setData(data.data)
      setTotal(data.total)
      setError(false)
    } catch (error) {
      console.log('erro ao pegar fila: ', error)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(page, tab, search)
  }, [page, tabIndex])

  useEffect(() => {
    loadStats()
  }, [tabIndex])

  const handleFilterList = useCallback(async (value: string) => {
    setSearch(value)
    setPage(1)
    await load(1, TABS[tabIndex], value)
  }, [load, tabIndex])

  const debouncedFetch = useCallback(
    debounce(async (value) => await handleFilterList(value), 1500),
    [handleFilterList]
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true)
    debouncedFetch(e.target.value)
  }

  const handleTabChange = (index: number) => {
    setTabIndex(index)
    setPage(1)
  }

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const { data } = await api.get('/queue-jobs/export', {
        responseType: 'arraybuffer',
        params: {
          tab,
          search: search || undefined
        }
      })

      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = tab === 'dlq' ? 'fila-dlq.xlsx' : 'fila-erros.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      toast({
        title: 'Erro ao exportar',
        position: 'top',
        isClosable: true,
        status: 'error',
        variant: 'solid'
      })
    } finally {
      setExporting(false)
    }
  }, [tab, search, toast])

  const handlePurge = useCallback(async () => {
    setConfirmPurge(false)
    setPurging(true)
    try {
      await api.post('/queue-jobs/purge')

      toast({
        title: 'Fila e DLQ limpas com sucesso',
        position: 'top',
        isClosable: true,
        status: 'success',
        variant: 'solid'
      })

      await load(1, tab, search)
      setPage(1)
      await loadStats()
    } catch (error) {
      toast({
        title: error?.response?.data?.message || 'Erro ao limpar a fila',
        position: 'top',
        isClosable: true,
        status: 'error',
        variant: 'solid'
      })
    } finally {
      setPurging(false)
    }
  }, [load, loadStats, tab, search, toast])

  const handleReprocessAll = useCallback(async () => {
    setConfirmReprocess(false)
    setReprocessing(true)
    try {
      const { data } = await api.post('/queue-jobs/reprocess-all')

      toast({
        title: `${data?.enqueued ?? 0} processo(s) enviado(s) para a fila`,
        position: 'top',
        isClosable: true,
        status: 'success',
        variant: 'solid'
      })

      await load(1, tab, search)
      setPage(1)
      await loadStats()
    } catch (error) {
      toast({
        title: error?.response?.data?.message || 'Erro ao reprocessar a fila',
        position: 'top',
        isClosable: true,
        status: 'error',
        variant: 'solid'
      })
    } finally {
      setReprocessing(false)
    }
  }, [load, loadStats, tab, search, toast])

  const handleRetry = useCallback(async (item: QueueJob) => {
    setRetrying(true)
    try {
      await api.post(`/queue-jobs/${item.id}/retry`)

      toast({
        title: 'Job reenviado para a fila',
        position: 'top',
        isClosable: true,
        status: 'success',
        variant: 'solid'
      })

      setSelectedItem(null)
      await load(page, tab, search)
      await loadStats()
    } catch (error) {
      toast({
        title: error?.response?.data?.message || 'Erro ao reprocessar',
        position: 'top',
        isClosable: true,
        status: 'error',
        variant: 'solid'
      })
    } finally {
      setRetrying(false)
    }
  }, [load, loadStats, page, tab, search, toast])

  const renderPayload = (payload?: string) => {
    try {
      return JSON.stringify(JSON.parse(payload || '{}'), null, 2)
    } catch {
      return payload || ''
    }
  }

  const renderTable = () => (
    loading
      ? (
      <Flex justify="center">
        <Spinner />
      </Flex>
        )
      : error
        ? (
      <Flex justify="center">
        <Text>Erro ao carregar a fila</Text>
      </Flex>
          )
        : (
      <>
        <Table colorScheme="blue">
          <Thead>
            <Tr>
              <Th textAlign="center" fontSize={12}>Nº Processo</Th>
              <Th textAlign="center" fontSize={12}>Prefeitura</Th>
              <Th textAlign="center" fontSize={12}>Status</Th>
              <Th textAlign="center" fontSize={12}>Tentativas</Th>
              <Th textAlign="center" fontSize={12}>Último Erro</Th>
              <Th textAlign="center" fontSize={12}>Enfileirado</Th>
              <Th w="8"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {data?.map((i) => (
              <Tr key={i.id}>
                <Td textAlign="center">
                  <Text fontWeight="bold" fontSize={14} color="wine.500">{i.prefecture_code}</Text>
                </Td>
                <Td textAlign="center">
                  <Text fontSize={14}>{i.prefecture}</Text>
                </Td>
                <Td textAlign="center">
                  <Badge colorScheme={STATUS_COLOR[i.status] || 'gray'}>
                    {STATUS_LABEL[i.status] || i.status}
                  </Badge>
                </Td>
                <Td textAlign="center">
                  <Text fontSize={14}>{i.attempts}</Text>
                </Td>
                <Td textAlign="center" maxWidth={250}>
                  {i.last_error
                    ? (
                    <Tooltip label={i.last_error.substring(0, 500)}>
                      <Text fontSize={12} noOfLines={2} color="red.500">{i.last_error}</Text>
                    </Tooltip>
                      )
                    : (
                    <Text fontSize={12}>-</Text>
                      )}
                </Td>
                <Td textAlign="center">
                  <Text fontSize={14}>{formatDate(i.queued_at)}</Text>
                </Td>
                <Td>
                  <Button
                    size="sm"
                    fontSize="sm"
                    bg="wine.200"
                    colorScheme="wine"
                    onClick={() => setSelectedItem(i)}
                  >
                    Ver dados
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {!data?.length && (
          <Flex justify="center" mt="8">
            <Text color="gray.500">Nenhum item encontrado</Text>
          </Flex>
        )}

        <Pagination
          totalCountOfRegisters={total}
          currentPage={page}
          onPageChange={setPage}
        />
      </>
          )
  )

  const showExport = tab === 'errors' || tab === 'dlq'

  return (
    <Can permissions={['queues.list', 'queues.create', 'queues.all']} page>
      <Box>
        {confirmPurge && (
          <AlertDialogCustom
            title="Limpar toda a fila"
            description="Isso remove TODAS as mensagens da fila principal e da DLQ no SQS. Essa ação não pode ser desfeita. Deseja continuar?"
            isOpen={confirmPurge}
            cancelFunction={() => setConfirmPurge(false)}
            nextFunction={handlePurge}
          />
        )}

        {confirmReprocess && (
          <AlertDialogCustom
            title="Reprocessar todos"
            description="Isso reenfileira todos os processos com automação ativa, como o cron faz. Deseja continuar?"
            isOpen={confirmReprocess}
            cancelFunction={() => setConfirmReprocess(false)}
            nextFunction={handleReprocessAll}
          />
        )}

        {!!selectedItem && (
          <Modal isOpen={!!selectedItem} onClose={() => setSelectedItem(null)} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader color="wine.primary">
                Processo {selectedItem.prefecture_code} — {selectedItem.prefecture}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text fontWeight="bold" fontSize={14} mb="1">Status</Text>
                <Badge colorScheme={STATUS_COLOR[selectedItem.status] || 'gray'} mb="4">
                  {STATUS_LABEL[selectedItem.status] || selectedItem.status}
                </Badge>

                <Text fontWeight="bold" fontSize={14} mb="1">Tentativas: {selectedItem.attempts}</Text>
                <Text fontSize={13} mb="4">
                  Enfileirado: {formatDate(selectedItem.queued_at)} | Iniciado: {formatDate(selectedItem.started_at)} | Finalizado: {formatDate(selectedItem.finished_at)}
                </Text>

                {!!selectedItem.last_error && (
                  <>
                    <Text fontWeight="bold" fontSize={14} mb="1" color="red.500">Último Erro</Text>
                    <Box bg="red.50" p="3" borderRadius={8} mb="4" maxHeight={200} overflowY="auto">
                      <Text fontSize={12} whiteSpace="pre-wrap" color="red.700">{selectedItem.last_error}</Text>
                    </Box>
                  </>
                )}

                <Text fontWeight="bold" fontSize={14} mb="1">Dados do Job</Text>
                <Box bg="gray.50" p="3" borderRadius={8} maxHeight={200} overflowY="auto">
                  <Text fontSize={12} whiteSpace="pre-wrap" fontFamily="mono">{renderPayload(selectedItem.payload)}</Text>
                </Box>
              </ModalBody>
              <ModalFooter>
                {(selectedItem.status === 'failed' || selectedItem.status === 'dlq') && (
                  <Can permissions={['queues.all']}>
                    <Button
                      bg="wine.primary"
                      _hover={{ bgColor: 'wine.200' }}
                      color="white"
                      mr="3"
                      isLoading={retrying}
                      leftIcon={<Icon as={FiRefreshCw} fontSize="16" />}
                      onClick={() => handleRetry(selectedItem)}
                    >
                      Reprocessar
                    </Button>
                  </Can>
                )}
                <Button onClick={() => setSelectedItem(null)}>Fechar</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}

        <Header />

        <Flex w="100%" my="6" maxWidth={1480} mx="auto" px={['4', '4', '6']}>
          <Sidebar />

          <Box flex="1" borderRadius={8} bg="white" p="8">
            <Flex mb="8" justify="space-between" align="center">
              <Heading size="lg" fontWeight="normal" color="wine.primary">
                Fila de Processos
              </Heading>

              <SearchBox placeholder="Buscar por nº do processo ou prefeitura..." onChangeFunction={handleInputChange} />

              <Can permissions={['queues.all']}>
                <Button
                  size="sm"
                  fontSize="sm"
                  ml="4"
                  bg="wine.primary"
                  _hover={{ bgColor: 'wine.200' }}
                  color="white"
                  isLoading={reprocessing}
                  leftIcon={<Icon as={FiPlayCircle} fontSize="16" />}
                  onClick={() => setConfirmReprocess(true)}
                >
                  Reprocessar todos
                </Button>

                <Button
                  size="sm"
                  fontSize="sm"
                  ml="4"
                  colorScheme="red"
                  isLoading={purging}
                  leftIcon={<Icon as={FiTrash2} fontSize="16" />}
                  onClick={() => setConfirmPurge(true)}
                >
                  Limpar fila
                </Button>
              </Can>

              {showExport && (
                <Button
                  size="sm"
                  fontSize="sm"
                  ml="4"
                  bg="wine.primary"
                  _hover={{ bgColor: 'wine.200' }}
                  color="white"
                  isLoading={exporting}
                  leftIcon={<Icon as={FiDownload} fontSize="16" />}
                  onClick={handleExport}
                >
                  Exportar XLS
                </Button>
              )}
            </Flex>

            <Tabs variant="enclosed" index={tabIndex} onChange={handleTabChange}>
              <TabList>
                <Tab>
                  Fila
                  {stats && <Badge ml="2" colorScheme="blue">{stats.counts.queue}</Badge>}
                </Tab>
                <Tab>
                  Erros
                  {stats && <Badge ml="2" colorScheme="red">{stats.counts.errors}</Badge>}
                </Tab>
                <Tab>
                  DLQ
                  {stats && <Badge ml="2" colorScheme="red">{stats.counts.dlq}</Badge>}
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel px="0">{renderTable()}</TabPanel>
                <TabPanel px="0">{renderTable()}</TabPanel>
                <TabPanel px="0">{renderTable()}</TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Flex>
      </Box>
    </Can>
  )
}

export { QueuesList }
